import { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronRight, ChevronLeft, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { createPortal } from 'react-dom';

export interface TourStep {
  target: string; // data-tour attribute value
  title: string;
  description: string;
  position: 'top' | 'bottom' | 'left' | 'right';
}

/**
 * MAINTAINER NOTE: Keep TOUR_STEPS in sync with the UI.
 * When adding, removing, or renaming UI sections, update:
 *  1. The matching `data-tour="..."` attribute on the target element
 *  2. The step entry below (title, description, position)
 *  3. The FAQ_DATA in FaqHowTo.tsx if the feature is also documented there
 */
const TOUR_STEPS: TourStep[] = [
  {
    target: 'sidebar-modes',
    title: 'AI Control Panel',
    description: 'Your command center. Eight buttons in a fixed strip: Check-In, Builder, Forms, Dashboard, Health, Approvals, Automation Rules, and Help. Visibility is role-gated — Managers+ see all eight, regular users see four.',
    position: 'right',
  },
  {
    target: 'sidebar-help',
    title: 'Help & What\'s New',
    description: 'Access searchable FAQ, guided tutorials, report problems, and see the latest feature updates. A pulsing badge appears when there\'s new content.',
    position: 'right',
  },
  {
    target: 'chat-input',
    title: 'AI Copilot Input',
    description: 'Type commands, ask questions, or describe tasks here. Use "@" to reference tasks, upload CSVs to bulk-import, or paste outlines. The copilot understands natural language — try "add task", "link tasks", or "reschedule". Use the microphone for voice input.',
    position: 'top',
  },
  {
    target: 'gantt-toolbar',
    title: 'Toolbar Controls',
    description: 'Zoom in/out, switch time granularity (Hour/Day/Week), filter by tags with View By, undo/redo changes, and access archive and trash. The Space picker lets you organize work across different workspaces.',
    position: 'bottom',
  },
  {
    target: 'gantt-chart',
    title: 'Gantt Chart',
    description: 'Your timeline lives here. Drag tasks to reschedule, click to select, double-click to open details. Connector lines show dependencies, and shaded areas mark non-working days. Right-click for quick actions. When you multi-select tasks, a bulk action toolbar pins to the top of the task list.',
    position: 'left',
  },
];

const STORAGE_KEY = 'ui-tour-completed';

interface UITourProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional override of the steps shown. Defaults to the global TOUR_STEPS. */
  steps?: TourStep[];
  /** Optional override of the localStorage key used to remember dismissal. */
  storageKey?: string;
}

export function UITour({ isOpen, onClose, steps, storageKey }: UITourProps) {
  const tourSteps = steps ?? TOUR_STEPS;
  const persistKey = storageKey ?? STORAGE_KEY;
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const [arrowStyle, setArrowStyle] = useState<React.CSSProperties>({});
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const step = tourSteps[currentStep];

  const positionTooltip = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(`[data-tour="${step.target}"]`);
    const tooltipW = 300;
    const tooltipH = tooltipRef.current?.offsetHeight || 180;

    if (!el) {
      // Center fallback
      setTooltipStyle({
        top: window.innerHeight / 2 - tooltipH / 2,
        left: window.innerWidth / 2 - tooltipW / 2,
        width: tooltipW,
      });
      setArrowStyle({ display: 'none' });
      return;
    }

    const rect = el.getBoundingClientRect();
    const gap = 12;

    let top = 0;
    let left = 0;
    const arrowPos: React.CSSProperties = {};

    switch (step.position) {
      case 'right':
        top = rect.top + rect.height / 2 - tooltipH / 2;
        left = rect.right + gap;
        arrowPos.left = -6;
        arrowPos.top = '50%';
        arrowPos.transform = 'translateY(-50%) rotate(45deg)';
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tooltipH / 2;
        left = rect.left - tooltipW - gap;
        arrowPos.right = -6;
        arrowPos.top = '50%';
        arrowPos.transform = 'translateY(-50%) rotate(45deg)';
        break;
      case 'bottom':
        top = rect.bottom + gap;
        left = rect.left + rect.width / 2 - tooltipW / 2;
        arrowPos.top = -6;
        arrowPos.left = '50%';
        arrowPos.transform = 'translateX(-50%) rotate(45deg)';
        break;
      case 'top':
        top = rect.top - tooltipH - gap;
        left = rect.left + rect.width / 2 - tooltipW / 2;
        arrowPos.bottom = -6;
        arrowPos.left = '50%';
        arrowPos.transform = 'translateX(-50%) rotate(45deg)';
        break;
    }

    // Clamp within viewport
    top = Math.max(8, Math.min(top, window.innerHeight - tooltipH - 8));
    left = Math.max(8, Math.min(left, window.innerWidth - tooltipW - 8));

    setTooltipStyle({ top, left, width: tooltipW });
    setArrowStyle(arrowPos);
  }, [step]);

  useEffect(() => {
    if (!isOpen) return;
    positionTooltip();
    window.addEventListener('resize', positionTooltip);
    return () => window.removeEventListener('resize', positionTooltip);
  }, [isOpen, currentStep, positionTooltip]);

  // Re-position after render when ref is available
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(positionTooltip);
    }
  }, [isOpen, currentStep, positionTooltip]);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleFinish();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep(prev => prev - 1);
  };

  const handleFinish = () => {
    if (dontShowAgain) {
      localStorage.setItem(persistKey, 'permanent');
    } else {
      localStorage.setItem(persistKey, 'true');
    }
    setCurrentStep(0);
    setDontShowAgain(false);
    onClose();
  };

  if (!isOpen || !step) return null;

  // Compute highlight rect
  const targetEl = document.querySelector(`[data-tour="${step.target}"]`);
  const highlightRect = targetEl?.getBoundingClientRect();

  return createPortal(
    <div className="fixed inset-0 z-[10000]">
      {/* Backdrop with cutout */}
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
        <defs>
          <mask id="tour-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {highlightRect && (
              <rect
                x={highlightRect.left - 4}
                y={highlightRect.top - 4}
                width={highlightRect.width + 8}
                height={highlightRect.height + 8}
                rx="8"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0" y="0" width="100%" height="100%"
          fill="hsl(var(--background) / 0.75)"
          mask="url(#tour-mask)"
          style={{ pointerEvents: 'all' }}
          onClick={handleFinish}
        />
      </svg>

      {/* Highlight ring */}
      {highlightRect && (
        <div
          className="absolute rounded-lg ring-2 ring-primary ring-offset-2 ring-offset-background pointer-events-none animate-pulse"
          style={{
            top: highlightRect.top - 4,
            left: highlightRect.left - 4,
            width: highlightRect.width + 8,
            height: highlightRect.height + 8,
          }}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="absolute bg-card border border-border rounded-xl shadow-xl p-4 animate-scale-in z-[10001]"
        style={tooltipStyle}
      >
        {/* Arrow */}
        <div
          className="absolute w-3 h-3 bg-card border border-border"
          style={{
            ...arrowStyle,
            clipPath: step.position === 'right' ? 'polygon(0 0, 0 100%, 100% 100%)' :
                      step.position === 'left' ? 'polygon(100% 0, 0 0, 100% 100%)' :
                      step.position === 'bottom' ? 'polygon(0 0, 100% 0, 50% 100%)' :
                      'polygon(0 100%, 100% 100%, 50% 0)',
          }}
        />

        {/* Close */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 h-6 w-6"
          onClick={handleFinish}
        >
          <X className="h-3.5 w-3.5" />
        </Button>

        {/* Content */}
        <div className="pr-6">
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
            Step {currentStep + 1} of {tourSteps.length}
          </p>
          <h3 className="font-semibold text-foreground text-sm mb-1.5">{step.title}</h3>
          <p className="text-xs text-muted-foreground leading-relaxed">{step.description}</p>
        </div>

        {/* Don't show again checkbox - only on last step */}
        {currentStep === tourSteps.length - 1 && (
          <label className="flex items-center gap-2 mt-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-border accent-primary cursor-pointer"
            />
            <span className="text-[11px] text-muted-foreground">Don't show again</span>
          </label>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-[11px] gap-1"
            onClick={handlePrev}
            disabled={currentStep === 0}
          >
            <ChevronLeft className="h-3 w-3" />
            Back
          </Button>
          <div className="flex gap-1">
            {tourSteps.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === currentStep ? 'w-4 bg-primary' : 'w-1.5 bg-muted-foreground/30'
                }`}
              />
            ))}
          </div>
          <Button
            size="sm"
            className="h-7 text-[11px] gap-1"
            onClick={handleNext}
          >
            {currentStep === tourSteps.length - 1 ? 'Finish' : 'Next'}
            {currentStep < tourSteps.length - 1 && <ChevronRight className="h-3 w-3" />}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function isTourCompleted(): boolean {
  const val = localStorage.getItem(STORAGE_KEY);
  return val === 'true' || val === 'permanent';
}

export function isTourPermanentlyDismissed(): boolean {
  return localStorage.getItem(STORAGE_KEY) === 'permanent';
}

export function resetTour(): void {
  localStorage.removeItem(STORAGE_KEY);
}
